import { z } from 'zod';
import { camadaSchema, valoresDaCamada, type ValoresDaCadeira } from './camadas.js';
import { enderecoSchema, ENDERECO_VAZIO } from './endereco.js';
import { etapaSchema, marcoSchema } from './etapas.js';
import { validarDocumento, validarTelefone } from './validacao.js';

export const tipoPessoaSchema = z.enum(['pj', 'pf']);
export type TipoPessoa = z.infer<typeof tipoPessoaSchema>;

/**
 * Titular nomeado da cadeira. Só existe em cadeira de pessoa jurídica: até três
 * C-levels ou diretores, com direito de uso individual e não simultâneo (Termo, cl. II).
 */
export const titularSchema = z.object({
  nome: z.string().trim().max(120).default(''),
  cargo: z.string().trim().max(80).default(''),
  cpf: z
    .string()
    .trim()
    .max(20)
    .default('')
    .refine((v) => v === '' || validarDocumento(v), { message: 'CPF inválido' }),
  contato: z.string().trim().max(160).default(''),
});
export type Titular = z.infer<typeof titularSchema>;

export const MAX_TITULARES = 3;

/** Campos que o cockpit envia ao criar ou atualizar um candidato. */
export const candidatoEntradaSchema = z
  .object({
    tipo: tipoPessoaSchema.default('pj'),
    /** Razão social (PJ) ou nome completo (PF). */
    razao: z.string().trim().max(160).default(''),
    /** CNPJ (PJ) ou CPF (PF), com ou sem máscara. */
    doc: z
      .string()
      .trim()
      .max(20)
      .default('')
      .refine((v) => v === '' || validarDocumento(v), { message: 'CPF ou CNPJ inválido' }),
    /**
     * Estruturado, não texto livre: o CEP preenche quase tudo e o que sobra para a pessoa
     * é número e complemento. Ver packages/shared/src/endereco.ts.
     */
    endereco: enderecoSchema.default(ENDERECO_VAZIO),
    telefone: z
      .string()
      .trim()
      .max(24)
      .default('')
      .refine((v) => v === '' || validarTelefone(v), { message: 'Telefone inválido' }),
    /** Representante legal — só se aplica a pessoa jurídica. */
    representante: z.string().trim().max(120).default(''),
    email: z.union([z.literal(''), z.string().trim().email()]).default(''),
    titulares: z.array(titularSchema).max(MAX_TITULARES).default([]),
    camada: camadaSchema.default('movimento'),
    /**
     * Número da cadeira. A faixa depende da camada e é validada no servidor, porque o
     * patch pode trazer só um dos dois campos — ver `validarNumeroDaCadeira`.
     */
    numero: z.string().trim().max(3).default(''),
    observacoes: z.string().trim().max(2000).default(''),
  })
  .strict();
export type CandidatoEntrada = z.infer<typeof candidatoEntradaSchema>;

/** Atualização parcial — o cockpit salva a ficha inteira, mas a API aceita PATCH. */
export const candidatoPatchSchema = candidatoEntradaSchema.partial().strict();
export type CandidatoPatch = z.infer<typeof candidatoPatchSchema>;

/**
 * Valores congelados no momento em que a Carta de Intenções foi emitida.
 *
 * A tabela de preços da comunidade pode mudar — renovação, segunda turma, correção. O que
 * o Proponente assinou, não. A partir da emissão, documentos e cobranças leem daqui e
 * nunca mais da tabela viva.
 */
export const valoresContratadosSchema = z.object({
  total: z.number().int().nonnegative(),
  sinal: z.number().int().nonnegative(),
  saldo: z.number().int().nonnegative(),
  camada: camadaSchema,
  congeladoEm: z.string().datetime(),
});
export type ValoresContratados = z.infer<typeof valoresContratadosSchema>;

export const candidatoSchema = candidatoEntradaSchema.extend({
  candidatoId: z.string(),
  /**
   * Referência do documento, "CI 007/2026". Gerada pelo servidor por contador atômico:
   * identificador de instrumento contratual não se digita, e duas fichas com a mesma
   * referência tornariam a conciliação impossível.
   */
  referencia: z.string(),
  etapa: etapaSchema,
  /** Marcos da esteira: id do marco → data ISO em que ocorreu. */
  marcos: z.record(marcoSchema, z.string().datetime()).default({}),
  /** Preenchido na emissão da Carta. Antes disso, vale a tabela vigente. */
  valoresContratados: valoresContratadosSchema.optional(),
  /**
   * Pendência financeira aberta: sinal estornado, chargeback ou cobrança vencida.
   * Enquanto existir, a cadeira segue bloqueada e a operação precisa decidir.
   */
  pendenciaFinanceira: z.string().max(300).optional(),
  /** Motivo registrado na recusa da curadoria ou na desistência. */
  motivoEncerramento: z.string().max(500).optional(),
  criadoEm: z.string().datetime(),
  criadoPor: z.string(),
  atualizadoEm: z.string().datetime(),
  atualizadoPor: z.string(),
});
export type Candidato = z.infer<typeof candidatoSchema>;

/**
 * Valores que valem para este candidato: o congelado no contrato, se já houver, senão a
 * tabela vigente. **Toda** exibição de valor, emissão de documento e cobrança passa por
 * aqui — chamar `valoresDaCamada` direto sobre um candidato é o caminho para imprimir
 * número diferente do que foi assinado.
 */
export function valoresVigentes(
  candidato: Pick<Candidato, 'camada' | 'valoresContratados'>,
): ValoresDaCadeira {
  const congelado = candidato.valoresContratados;
  if (congelado) {
    return { total: congelado.total, sinal: congelado.sinal, saldo: congelado.saldo };
  }
  return valoresDaCamada(candidato.camada);
}

/** A camada não pode mudar depois de o valor virar contrato. */
export function podeTrocarDeCamada(candidato: Pick<Candidato, 'valoresContratados'>): boolean {
  return !candidato.valoresContratados;
}

/**
 * Campos obrigatórios para emitir cobrança: sem eles o Asaas recusa a criação do cliente.
 * Retorna a lista de pendências em linguagem de negócio (vazia = pode cobrar).
 */
export function pendenciasParaCobranca(c: Pick<Candidato, 'razao' | 'doc' | 'email'>): string[] {
  const faltando: string[] = [];
  if (!c.razao) faltando.push('nome ou razão social');
  if (!c.doc) faltando.push('CPF ou CNPJ');
  else if (!validarDocumento(c.doc)) faltando.push('CPF ou CNPJ válido');
  if (!c.email) faltando.push('e-mail');
  return faltando;
}
